// Settings, behind the gear.
//
// **Not a fourth door.** The doors are ways to start playing and exactly one of
// them unfolds at a time; this is not one of those, so it does not close a door
// and a door does not close it. It sits in the title row because that is where
// a thing about the *app* belongs — beside "How to play" rather than among the
// things that launch a game.

import type { PiecePack } from "src/client/ai/render/models";
import { PACK_CHOICES } from "../packPref";
import { SoundSettings } from "../SoundSettings";
import { Panel, SectionTitle, SettingRow, Text, Toggle, styled } from "../kit";
import { ChipChoice } from "./ChipChoice";

const Plate = styled(Panel)(({ theme }) => ({
  padding: theme.kit.space.md,
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.md
}));

export interface TelemetryGate {
  consent: { answer: string };
  answer(agreed: boolean): void;
}

export function SettingsPanel({
  pack,
  onPack,
  telemetry
}: {
  pack: PiecePack;
  onPack(pack: PiecePack): void;
  /**
   * The telemetry answer and a way to change it.
   *
   * Undefined where there is no gate — a laptop and an e2e smoke never submit
   * anything, so a switch there would be a control over nothing. The consent
   * screen promises this switch exists, so the two have to appear together.
   */
  telemetry?: TelemetryGate;
}) {
  const sending = telemetry?.consent.answer === "agreed";
  return (
    <Plate solid data-testid="settings-panel">
      <div>
        <ChipChoice
          label="Pieces"
          value={pack}
          onChange={onPack}
          options={PACK_CHOICES}
          testPrefix="pack"
        />
        <Text tone="dim" size={11.5} block style={{ marginTop: 6 }}>
          Applies to the next game you start.
        </Text>
      </div>

      {/*
        **Here for now, and it takes no props so that it need not stay.** The
        gear is the only settings surface the front door has; the volume
        controls belong to a proper one, and this component holds its own prefs
        and applies them itself precisely so moving it is a line of JSX rather
        than a refactor.
      */}
      <SoundSettings />

      {/*
        The switch the consent screen promises.

        **A switch and not a second copy of the disclosure.** The full
        explanation belongs where the decision is first made; repeating it here
        would be two descriptions of one thing to keep in step, which is the
        drift this codebase keeps paying for. What it does carry is the one
        sentence that changes with the answer, so the control says what state it
        is in rather than only what it does.
      */}
      {telemetry && (
        <div>
          <SectionTitle>Your games</SectionTitle>
          <SettingRow>
            <Text size={13}>
              {sending ? "Sending finished games" : "Not sending anything"}
            </Text>
            <Toggle
              data-testid="telemetry-toggle"
              checked={sending}
              onChange={(on) => telemetry.answer(on)}
              aria-label="Send finished games"
            />
          </SettingRow>
          <Text tone="dim" size={12} block style={{ lineHeight: 1.45 }}>
            {sending
              ? "When a game ends we get a copy of it — every move, replayable."
              : "Nothing about your games leaves this machine."}
          </Text>
        </div>
      )}
    </Plate>
  );
}
